// ---------------------------------------------------------------------------
// fetchPresetsViaWorker — list all design-system presets from the Worker.
// Uses platform.cogo.xyz /api/design-systems (no direct Supabase access).
// ---------------------------------------------------------------------------

import { getStudioAccessToken } from "@/services/auth-provider";
import {
  cogoPlatformApiUrl,
  withCogoDevProxyHeaderRecord,
} from "@/services/cogo-platform-config";
import {
  BASE_COLOR_OPTIONS,
  CHART_OPTIONS,
  FONT_OPTIONS,
  RADIUS_OPTIONS,
  STYLE_OPTIONS,
  THEME_OPTIONS,
  type DesignSystemState,
} from "./design-system-options";

export interface PresetListItem {
  id: string;
  name: string;
  description: string;
  tokens?: DesignSystemState;
}

async function getJwt(): Promise<string | null> {
  return getStudioAccessToken();
}

function parseDesignSystemState(value: unknown): DesignSystemState | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }
  const state = value as Record<string, unknown>;
  const inOptions = (
    candidate: unknown,
    options: ReadonlyArray<{ value: string }>,
  ): candidate is string =>
    typeof candidate === "string" &&
    options.some((option) => option.value === candidate);
  if (
    !inOptions(state.style, STYLE_OPTIONS) ||
    !inOptions(state.baseColor, BASE_COLOR_OPTIONS) ||
    !inOptions(state.theme, THEME_OPTIONS) ||
    !inOptions(state.chart, CHART_OPTIONS) ||
    !inOptions(state.radius, RADIUS_OPTIONS) ||
    !inOptions(state.font, FONT_OPTIONS)
  ) {
    return undefined;
  }
  return state as unknown as DesignSystemState;
}

export async function fetchPresetsViaWorker(): Promise<PresetListItem[]> {
  const jwt = await getJwt();
  if (!jwt) return [];
  try {
    const res = await fetch(cogoPlatformApiUrl("/api/design-systems"), {
      headers: withCogoDevProxyHeaderRecord({ Authorization: `Bearer ${jwt}` }),
    });
    if (!res.ok) return [];
    const arr = (await res.json()) as PresetListItem[];
    const seenNames = new Set<string>();
    return arr.flatMap((preset) => {
      if (
        typeof preset.id !== "string" ||
        typeof preset.name !== "string" ||
        !preset.name.trim()
      ) {
        return [];
      }
      const name = preset.name.trim();
      const normalizedName = name.toLocaleLowerCase();
      if (seenNames.has(normalizedName)) return [];
      seenNames.add(normalizedName);
      return [
        {
          id: preset.id,
          name,
          description:
            typeof preset.description === "string" ? preset.description : "",
          tokens: parseDesignSystemState(preset.tokens),
        },
      ];
    });
  } catch {
    return [];
  }
}
